import { fetchAgents } from '@/api/agents'
import { downloadCsv } from '@/lib/csvExport'
import {
  formatCpuSpeed,
  formatLastSeen,
  formatOs,
  formatRam,
} from '@/lib/formatAgent'
import { TRAFFIC } from '@/lib/trafficLight'

const AV_STATUS_LABELS = {
  protected: 'Protected',
  not_detected: 'Not detected',
  no_data: 'No software data',
}

const EXPORT_PAGE_SIZE = 500

function postureLabel(status) {
  return TRAFFIC[status]?.label || status || ''
}

/** Plain-text cell value for a Devices table column. */
export function deviceExportCellValue(columnId, agent) {
  switch (columnId) {
    case 'hostname':
      return agent.hostname || `Device ${agent.atera_id}`
    case 'display_name':
      return agent.display_name || ''
    case 'customer':
      return agent.customer_name || ''
    case 'os_name':
      return formatOs(agent)
    case 'os_build':
      return agent.os_build || ''
    case 'machine_type':
      return agent.machine_type || ''
    case 'cpu_name': {
      const speed = formatCpuSpeed(agent.cpu_speed_mhz)
      return [agent.cpu_name, speed].filter(Boolean).join(' · ')
    }
    case 'cpu_cores':
      return agent.cpu_cores ?? ''
    case 'ram_mb':
      return formatRam(agent.ram_mb)
    case 'domain':
      return agent.domain || ''
    case 'last_logged_user':
      return agent.last_logged_user || ''
    case 'agent_version':
      return agent.agent_version || ''
    case 'online_status':
      return agent.online_status ? 'Online' : 'Offline'
    case 'security_status':
      return postureLabel(agent.security_status)
    case 'hardware_status':
      return postureLabel(agent.hardware_status)
    case 'toolkit_status':
      return postureLabel(agent.toolkit_status)
    case 'last_seen':
      return formatLastSeen(agent.last_seen)
    case 'last_reboot_time':
      return formatLastSeen(agent.last_reboot_time)
    case 'windows_serial_number':
      return agent.windows_serial_number || ''
    case 'folder_name':
      return agent.folder_name || ''
    case 'app_view_url':
      return agent.app_view_url || ''
    case 'computer_description':
      return agent.computer_description || ''
    case 'reported_from_ip':
      return agent.reported_from_ip || ''
    case 'system_drive':
      return agent.system_drive || ''
    case 'created_at_atera':
      return formatLastSeen(agent.created_at_atera)
    case 'modified_at_atera':
      return formatLastSeen(agent.modified_at_atera)
    case 'av_product':
      return agent.av_product || ''
    case 'av_status':
      return AV_STATUS_LABELS[agent.av_status] || agent.av_status || ''
    default:
      return agent[columnId] ?? ''
  }
}

/** Fetch every device matching the current Devices page filters. */
export async function fetchAllFilteredAgents({
  search,
  customerIds,
  onlineFilter,
  sortBy,
  sortDir,
}) {
  const params = {
    page: 1,
    page_size: EXPORT_PAGE_SIZE,
    search: search || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
  }
  if (onlineFilter === 'online') params.online = true
  if (onlineFilter === 'offline') params.online = false

  const first = await fetchAgents(params, customerIds)
  const items = [...(first.items || [])]
  const pages = first.pages || 1

  for (let page = 2; page <= pages; page += 1) {
    const next = await fetchAgents({ ...params, page }, customerIds)
    items.push(...(next.items || []))
  }

  return items
}

export function exportDevicesCsv(agents, columns) {
  const headers = columns.map((col) => col.label)
  const rows = agents.map((agent) =>
    columns.map((col) => deviceExportCellValue(col.id, agent)),
  )
  const stamp = new Date().toISOString().slice(0, 10)
  downloadCsv(`devices-${stamp}.csv`, headers, rows)
}
